import { useEffect, useState } from "react"

// Tailwind's `md` breakpoint — the single source of truth for "desktop" in the
// app's responsive rules (e.g. the Sidebar rail's `hidden md:flex`).
const DESKTOP_QUERY = "(min-width: 768px)"

/**
 * True when the viewport is at/above Tailwind's `md` breakpoint.
 *
 * The app is otherwise CSS-only for responsiveness, but the Tasks tab needs a
 * JS branch: the Kanban board mounts one infinite query PER status column, so
 * merely CSS-hiding it on mobile would still fire those queries. This hook lets
 * the host render ONLY the single-query List on mobile and never mount the board.
 * SSR-guarded (defaults to desktop) — in the Vite SPA `window` exists at mount,
 * so the initial value is already correct and there's no flash.
 */
export function useIsDesktop(): boolean {
	const [isDesktop, setIsDesktop] = useState<boolean>(() =>
		typeof window === "undefined"
			? true
			: window.matchMedia(DESKTOP_QUERY).matches,
	)

	useEffect(() => {
		if (typeof window === "undefined") return
		const mql = window.matchMedia(DESKTOP_QUERY)
		const onChange = () => setIsDesktop(mql.matches)
		onChange()
		mql.addEventListener("change", onChange)
		return () => mql.removeEventListener("change", onChange)
	}, [])

	return isDesktop
}
